import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from './Loader';
import './ProfileView.css';

const API = 'https://mykaddle-api.onrender.com/api';
const MAX_COMMENT_LENGTH = 180;

const TutorPublicProfile = () => {
  const navigate = useNavigate();
  const params = useParams();
  const textareaRef = useRef(null);

  // Support: in-app navigation ('tutorId'/'id') and the public
  // slug-based route (mykaddle.com/:slug) added later.
  const tutorId = params.tutorId || params.id;
  const slug = params.slug;
  const isSlugRoute = !!slug;
  const identifier = slug || tutorId;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const currentUser = (() => {
    try {
      const user = localStorage.getItem('user');
      return user && user !== 'undefined' ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  })();

  // ── Guard: Redirect if no identifier at all ──
  useEffect(() => {
    if (!identifier || identifier === 'undefined') {
      console.error('No tutor identifier provided — redirecting');
      navigate(-1);
    }
  }, [identifier, navigate]);

  // ── Detect screen size ──
  useEffect(() => {
    const checkScreen = () => {
      setIsMobile(window.innerWidth < 768 || window.innerHeight < 600);
    };
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // ── Fetch profile + review check ──
  const fetchProfile = useCallback(async () => {
    if (!identifier || identifier === 'undefined') return;

    setLoading(true);
    try {
      // Both endpoints now return the same flattened shape:
      // { tutorId, name, bio, profilePic, institution, qualification,
      //   availability, likeCount, totalGroups, totalMembers, reviews, isBlocked }
      const endpoint = isSlugRoute
        ? `${API}/tutor/public/${identifier}`
        : `${API}/tutor/profile/${identifier}`;

      const res = await fetch(endpoint);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch tutor');
      setProfile(data);

      // Check if current user has reviewed this tutor.
      // Uses the real Mongo tutorId from the fetched profile — not the
      // slug — since has-reviewed is keyed by tutorProfileId.
      if (token && data?.tutorId) {
        try {
          const reviewCheck = await fetch(
            `${API}/tutee/has-reviewed/${data.tutorId}`,
            { headers: authHeaders }
          );
          const reviewData = await reviewCheck.json();
          setHasReviewed(reviewData.hasReviewed || false);
        } catch {
          // Non-critical — user might not be logged in
          setHasReviewed(false);
        }
      }
    } catch (err) {
      console.error('Failed to load tutor profile:', err);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [identifier, isSlugRoute, token]);

  useEffect(() => {
    if (identifier && identifier !== 'undefined') {
      fetchProfile();
    }
  }, [fetchProfile, identifier]);

  // ── Keyboard: Esc closes modal ──
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && showReviewModal) closeReviewModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showReviewModal]);

  // ── Auto-focus textarea when modal opens ──
  useEffect(() => {
    if (showReviewModal && textareaRef.current && !hasReviewed) {
      setTimeout(() => textareaRef.current?.focus(), 120);
    }
  }, [showReviewModal, hasReviewed]);

  // ── Review handlers ──
  const openReviewModal = () => {
    setReviewText('');
    setSubmitError('');
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSubmitError('');
  };

  const handleReviewChange = (e) => {
    if (e.target.value.length <= MAX_COMMENT_LENGTH) setReviewText(e.target.value);
  };

  const submitReview = async () => {
    const trimmed = reviewText.trim();
    if (!trimmed) { setSubmitError('Please write something before submitting.'); return; }
    if (trimmed.length < 10) { setSubmitError('Comment is too short (min 10 characters).'); return; }

    setSubmitting(true);
    setSubmitError('');

    try {
      // Always submit against the real tutor tutorId, not the slug.
      const res = await fetch(`${API}/tutor/profile/${profile.tutorId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ comment: trimmed })
      });
      const data = await res.json();

      if (!res.ok) { setSubmitError(data.message || 'Failed to submit review.'); return; }

      // Optimistically prepend the new comment
      setProfile(prev => ({
        ...prev,
        reviews: [
          {
            tuteeName: currentUser?.displayName || 'You',
            comment: trimmed,
            createdAt: new Date().toISOString()
          },
          ...(prev.reviews || [])
        ]
      }));

      setHasReviewed(true);
      closeReviewModal();
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const charsLeft = MAX_COMMENT_LENGTH - reviewText.length;
  const counterCls = charsLeft <= 20 ? 'warn' : '';

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ── Loading state with Loader ──
  if (loading) {
    return (
      <div className="tpp-dashboard-container">
        <div className="tpp-almighty-wrapper">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <Loader size="7em" />
            <span style={{ color: 'var(--tpp-muted)', fontSize: '0.9rem' }}>Tutor Profile</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Not found state ──
  if (!profile) {
    return (
      <div className="tpp-dashboard-container">
        <div className="tpp-almighty-wrapper">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '1rem' }}>
            <div style={{ color: 'var(--tpp-muted)', fontSize: '0.9rem' }}>Tutor not found.</div>
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: '0.5rem 1.2rem', borderRadius: '2rem', border: 'none',
                background: 'var(--tpp-lime)', color: '#4a4b0a', fontSize: '0.85rem',
                fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif"
              }}
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const reviews = profile.reviews || [];
  const isBlocked = profile.isBlocked || false;

  // Both getTutorPublicProfile and getPublicProfileBySlug now return the
  // same flattened shape — profilePic is a plain URL string,
  // qualification/institution are flat fields (already resolved from
  // education[0] on the backend), so no nested access needed here.
  const profilePicUrl = profile.profilePic || null;
  const qualification = profile.qualification || 'Qualification';
  const institution = profile.institution || '';

  // ═══════════════════════════════════════
  // MOBILE LAYOUT (File 2)
  // ═══════════════════════════════════════
  if (isMobile) {
    return (
      <div className="tpp-dashboard-container tpp-mobile">
        <div className="tpp-almighty-wrapper tpp-mobile-wrapper">
          {/* Top bar */}
          <div className="tpp-top-bar tpp-mobile-top-bar">
            <div className="tpp-top-left-header">
              <div className="tpp-header-row">
                <span className="tpp-page-title tpp-mobile-page-title">MyKaddle Tutor</span>
              </div>
              <p className="tpp-page-subtitle">This page is optimised for larger screens. For the full experience, use a tablet, laptop, or desktop. On mobile, you can browse available tutors.</p>
            </div>
          </div>

          <div className="tpp-main-content tpp-mobile-main-content">
            <div className="tpp-left-column">
              {/* Profile Box */}
              <div className="tpp-profile-container">
                <div className="tpp-box-profile">
                  <div className="tpp-box-pic" style={{ backgroundImage: profilePicUrl ? `url(${profilePicUrl})` : "url('default.png')" }}></div>
                  <div className="tpp-box-ttname">{profile.name || profile.displayName || 'Tutor'}</div>
                  <div className="tpp-box-qual">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                        <path d="M14.217 3.5a5.17 5.17 0 0 0-4.434 0L3.092 6.637c-1.456.682-1.456 3.044 0 3.726l6.69 3.137a5.17 5.17 0 0 0 4.435 0l6.691-3.137c1.456-.682 1.456-3.044 0-3.726zM22 8.5v5" />
                        <path d="M5 11.5v5.125C5 19.543 9.694 21 12 21s7-1.457 7-4.375V11.5" />
                      </g>
                    </svg>
                    {qualification}
                  </div>
                  {institution && (
                    <div className="tpp-box-institution">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                        <path d="M0 0h256v256H0z" fill="none" />
                        <path fill="currentColor" d="M104 34H56a14 14 0 0 0-14 14v160a14 14 0 0 0 14 14h48a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14M54 78h52v100H54Zm2-32h48a2 2 0 0 1 2 2v18H54V48a2 2 0 0 1 2-2m48 164H56a2 2 0 0 1-2-2v-18h52v18a2 2 0 0 1-2 2m125.7-15L196.51 37.16a14 14 0 0 0-16.63-10.85l-46.81 10.06A14.09 14.09 0 0 0 122.3 53l33.19 157.81a14 14 0 0 0 6.1 8.9a13.85 13.85 0 0 0 7.57 2.26a13.6 13.6 0 0 0 3-.32l46.81-10.05A14.09 14.09 0 0 0 229.7 195m-82.81-83.32l50.73-10.9l14.12 67.16L161 178.81Zm-6.63-31.56L191 69.19L195.15 89l-50.73 10.9Zm-4.66-32l46.8-10.05a2 2 0 0 1 .42 0a1.9 1.9 0 0 1 1.05.32a2 2 0 0 1 .89 1.31l3.75 17.82l-50.72 10.82l-3.74-17.78a2.07 2.07 0 0 1 1.55-2.46Zm80.81 151.8L169.6 210a1.92 1.92 0 0 1-1.47-.27a2 2 0 0 1-.89-1.31l-3.75-17.82l50.72-10.9l3.79 17.73a2.07 2.07 0 0 1-1.59 2.47Z" />
                      </svg>
                      {institution}
                    </div>
                  )}
                  <div className="tpp-box-bio">{profile.bio || 'No bio available.'}</div>
                </div>
              </div>

              {/* Availability */}
              <div className="tpp-box-avail">
                <div className="tpp-avail-title">Availability</div>
                <div className="tpp-avail-content">
                  <div className="tpp-days-grid">
                    {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                      const short = day.slice(0, 3);
                      const isActive = Array.isArray(profile.availability) && profile.availability.some(d => d.toLowerCase() === day.toLowerCase());
                      return (
                        <div key={day} className={`tpp-day-btn ${isActive ? 'active' : ''}`}>{short}</div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Engagements */}
            <div className="tpp-engagements-box">
              <p className="tpp-eng-head">Engagements</p>
              <div className="tpp-eng-likes">Total likes: {profile.likeCount || 0}</div>
              <button className="tpp-eng-reviews-btn" onClick={openReviewModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none"/>
                  <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3s3-1.34 3-3s-1.34-3-3-3"/>
                </svg>
                Reviews
              </button>
            </div>
          </div>

          {/* Mobile Review Modal (read-only bottom sheet) */}
          {showReviewModal && (
            <div className="tpp-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget) closeReviewModal(); }}>
              <div className="tpp-modal tpp-mobile-modal">
                <button className="tpp-modal-close" onClick={closeReviewModal}>✕</button>
                <div className="tpp-modal-header">
                  <div className="tpp-modal-title">Reviews</div>
                  <div className="tpp-modal-sub">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</div>
                </div>
                <div className="tpp-reviews-list">
                  {reviews.length === 0 ? (
                    <div className="tpp-no-reviews">No reviews yet.</div>
                  ) : (
                    reviews.map((r, i) => (
                      <div key={i} className="tpp-review-item">
                        <div className="tpp-review-header">
                          <span className="tpp-review-user">{r.tuteeName || 'Anonymous'}</span>
                          <span className="tpp-review-date">{formatDate(r.createdAt)}</span>
                        </div>
                        <p className="tpp-review-text">{r.comment}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="tpp-modal-safe-area"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // DESKTOP LAYOUT (File 1)
  // ═══════════════════════════════════════
  return (
    <div className="tpp-dashboard-container tpp-desktop">
      <div className="tpp-almighty-wrapper">

        {/* Top bar */}
        <div className="tpp-top-bar">
          <div className="tpp-top-left-header">
            <div className="tpp-header-row">
              <button className="tpp-back-icon-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none"/>
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
                </svg>
              </button>
              <span className="tpp-page-title">Tutor Profile</span>
            </div>
          </div>
        </div>

        <div className="tpp-main-content">
          {/* Left Column */}
          <div className="tpp-left-column">
            <div className="tpp-profile-container">
              <div className="tpp-box-profile">
                <div className="tpp-box-pic" style={{ backgroundImage: profilePicUrl ? `url(${profilePicUrl})` : "url('default.png')" }}></div>
                <div className="tpp-box-ttname">{profile.name || profile.displayName || 'Tutor'}</div>
                <div className="tpp-box-qual">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                      <path d="M14.217 3.5a5.17 5.17 0 0 0-4.434 0L3.092 6.637c-1.456.682-1.456 3.044 0 3.726l6.69 3.137a5.17 5.17 0 0 0 4.435 0l6.691-3.137c1.456-.682 1.456-3.044 0-3.726zM22 8.5v5" />
                      <path d="M5 11.5v5.125C5 19.543 9.694 21 12 21s7-1.457 7-4.375V11.5" />
                    </g>
                  </svg>
                  {qualification}
                </div>
                {institution && (
                  <div className="tpp-box-institution">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                      <path d="M0 0h256v256H0z" fill="none" />
                      <path fill="currentColor" d="M104 34H56a14 14 0 0 0-14 14v160a14 14 0 0 0 14 14h48a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14M54 78h52v100H54Zm2-32h48a2 2 0 0 1 2 2v18H54V48a2 2 0 0 1 2-2m48 164H56a2 2 0 0 1-2-2v-18h52v18a2 2 0 0 1-2 2m125.7-15L196.51 37.16a14 14 0 0 0-16.63-10.85l-46.81 10.06A14.09 14.09 0 0 0 122.3 53l33.19 157.81a14 14 0 0 0 6.1 8.9a13.85 13.85 0 0 0 7.57 2.26a13.6 13.6 0 0 0 3-.32l46.81-10.05A14.09 14.09 0 0 0 229.7 195m-82.81-83.32l50.73-10.9l14.12 67.16L161 178.81Zm-6.63-31.56L191 69.19L195.15 89l-50.73 10.9Zm-4.66-32l46.8-10.05a2 2 0 0 1 .42 0a1.9 1.9 0 0 1 1.05.32a2 2 0 0 1 .89 1.31l3.75 17.82l-50.72 10.82l-3.74-17.78a2.07 2.07 0 0 1 1.55-2.46Zm80.81 151.8L169.6 210a1.92 1.92 0 0 1-1.47-.27a2 2 0 0 1-.89-1.31l-3.75-17.82l50.72-10.9l3.79 17.73a2.07 2.07 0 0 1-1.59 2.47Z" />
                    </svg>
                    {institution}
                  </div>
                )}
                <div className="tpp-box-bio">{profile.bio || 'No bio available.'}</div>
              </div>
            </div>

            {/* Availability */}
            <div className="tpp-box-avail">
              <div className="tpp-avail-title">Availability</div>
              <div className="tpp-avail-content">
                <div className="tpp-days-grid">
                  {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                    const isActive = Array.isArray(profile.availability) && profile.availability.some(d => d.toLowerCase() === day.toLowerCase());
                    return (
                      <div key={day} className={`tpp-day-btn ${isActive ? 'active' : ''}`}>{day}</div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Engagements */}
          <div className="tpp-engagements-box">
            <p className="tpp-eng-head">Engagements</p>
            <div className="tpp-eng-likes">Total likes: {profile.likeCount || 0}</div>
            <button className="tpp-eng-reviews-btn" onClick={openReviewModal}>
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3s3-1.34 3-3s-1.34-3-3-3"/>
              </svg>
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Review Modal */}
      {showReviewModal && (
        <div className="tpp-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget) closeReviewModal(); }}>
          <div className="tpp-modal">
            <button className="tpp-modal-close" onClick={closeReviewModal}>✕</button>
            <div className="tpp-modal-header">
              <div className="tpp-modal-title">Reviews</div>
              <div className="tpp-modal-sub">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</div>
            </div>

            {/* Reviews list */}
            <div className="tpp-reviews-list">
              {reviews.length === 0 ? (
                <div className="tpp-no-reviews">No reviews yet. Be the first to leave one!</div>
              ) : (
                reviews.map((r, i) => (
                  <div key={i} className="tpp-review-item">
                    <div className="tpp-review-header">
                      <span className="tpp-review-user">{r.tuteeName || 'Anonymous'}</span>
                      <span className="tpp-review-date">{formatDate(r.createdAt)}</span>
                    </div>
                    <p className="tpp-review-text">{r.comment}</p>
                  </div>
                ))
              )}
            </div>

            {/* Review form — only on desktop, only if user hasn't reviewed */}
            {hasReviewed ? (
              <div className="tpp-review-submitted-msg">✓ You've already left a review</div>
            ) : !currentUser ? (
              <div className="tpp-review-submitted-msg">Log in to leave a review.</div>
            ) : (
              <div className="tpp-review-form">
                <div className="tpp-review-form-title">Leave a review</div>
                <textarea
                  ref={textareaRef}
                  className="tpp-review-textarea"
                  maxLength={MAX_COMMENT_LENGTH}
                  placeholder="Share your experience with this tutor…"
                  value={reviewText}
                  onChange={handleReviewChange}
                />
                <div className="tpp-review-form-footer">
                  <span className={`tpp-review-char-count ${counterCls}`}>
                    {charsLeft}
                  </span>
                  {submitError && <span className="tpp-submit-error">{submitError}</span>}
                  <button
                    className="tpp-review-submit-btn"
                    onClick={submitReview}
                    disabled={submitting || reviewText.trim().length < 10}
                  >
                    {submitting ? 'Submitting…' : 'Submit Review'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorPublicProfile;